import { useEffect, useState } from "react";
import styled from "styled-components";
import { Form, Table } from "react-bootstrap";

import { NameID } from "common/types/NameID";
import { MapArea, MapPoint } from "common/types/MapArea";
import { MapAreaList } from "common/components/molecules/MapAreaList";
import { NameIDList } from "common/components/molecules/NameIDList";
import { SelectMapAreaDlg } from "common/components/map/SelectMapAreaDlg";

import { useAreas } from "common/hooks/useAreas";

import MyTextArea from "common/components/atoms/MyTextArae";
import MyCheckbox from "common/components/atoms/MyCheckbox";
import MyNumberInput from "common/components/atoms/MyNumberInput";
import { MapAreaText } from "common/components/atoms/MapAreaText";

import { CheckListAnswer, CheckListItem } from "../types/WorkReportData";
import { MultiLineText } from "common/components/atoms/MultiLineText";
import FunctionButton from "common/components/atoms/FunctionButton";
import React from "react";

type Props = {
  metaLists: NameID[];
  checkList: CheckListItem[];
  areas: MapArea[];
  readonly: boolean;
  isMapAvailable: boolean;
  addAnswerRequested: (item: CheckListItem) => void;
  removeAnswerRequested: (answer: CheckListAnswer, item: CheckListItem) => void;
};
export const CheckListPanel = (props: Props) => {
  const { metaLists, checkList, areas, readonly, isMapAvailable } = props;
  const { toAreaString, toPointString } = useAreas();

  const [allChecked, setAllChecked] = useState(false);
  const [mapTarget, setMapTarget] = useState<CheckListAnswer | null>(null);

  const getListBody = (ans: CheckListAnswer, item: CheckListItem, isFirst: boolean) => {
    return (
      <>
        <td>
          {readonly ? (
            <MultiLineText text={ans.memo} />
          ) : (
            <SMyTextArea
              readonly={readonly}
              initialValue={ans.memo}
              borderAlways={false}
              changed={(value) => (ans.memo = value)}
            />
          )}
        </td>
        <td style={{ alignContent: "center" }}>
          {readonly ? (
            <MapAreaText text={ans.point ? toPointString(ans.point) : toAreaString(ans.areas)} />
          ) : isMapAvailable ? (
            <MapAreaList
              areas={ans.areas}
              point={ans.point}
              maxHeight="150px"
              readonly={readonly}
              onClick={() => setMapTarget(ans)}
            />
          ) : (
            <NameIDList
              title="チェックリスト地区"
              lists={areas.map((x) => x.area)}
              selecteds={ans.areas ?? []}
              stringWhenEmpty="選択なし"
              borderAlways={true}
              style={{ fontSize: "14px" }}
              selectedChanged={(vals) => {
                ans.areas = vals;
                ans.point = undefined;
              }}
            />
          )}
        </td>
        <td>
          {readonly ? (
            <MultiLineText text={ans.areaDetail} />
          ) : (
            <SMyTextArea
              readonly={readonly}
              initialValue={ans.areaDetail}
              changed={(value) => (ans.areaDetail = value)}
              borderAlways={false}
            />
          )}
        </td>
        <td style={{ alignContent: "center" }}>
          <MyNumberInput
            initialValue={ans.count}
            readonly={readonly}
            changed={(value) => (ans.count = value)}
            style={{ padding: "0.4rem 0", fontSize: "14px" }}
          />
        </td>
        {!readonly && (
          <td style={{ alignContent: "center" }}>
            {isFirst ? (
              <FunctionButton
                tooltip="行を追加"
                size="sm"
                onClick={() => props.addAnswerRequested(item)}
              >
                <i className="fa fa-plus" />
              </FunctionButton>
            ) : (
              <FunctionButton
                tooltip="行を削除"
                size="sm"
                onClick={() => props.removeAnswerRequested(ans, item)}
              >
                <i className="fas fa-minus" />
              </FunctionButton>
            )}
          </td>
        )}
      </>
    );
  };

  const onCheckListChanged = () => {
    setAllChecked(checkList.every((x) => x.checked));
  };

  const onAllCheckChanged = (value: boolean) => {
    checkList.forEach((x) => (x.checked = value));
    setAllChecked(value);
  };

  useEffect(() => {
    onCheckListChanged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkList]);

  return (
    <>
      <RootDiv>
        <STable bordered hover>
          <thead>
            <tr className="text-center">
              <th style={{ width: "40px" }}>
                {!readonly && (
                  <Form.Check
                    checked={allChecked}
                    onChange={(e) => onAllCheckChanged(e.currentTarget?.checked)}
                  />
                )}
              </th>
              <th>No</th>
              <Sth3 className="col-md-*">項目</Sth3>
              <Sth3 className="col-md-*">備考</Sth3>
              <Sth2 className="col-md-2">地区</Sth2>
              <Sth2 className="col-md-2">地区詳細</Sth2>
              <Sth1 className="col-md-1">件数</Sth1>
              {!readonly && <th style={{ minWidth: "50px" }}>操作</th>}
            </tr>
          </thead>
          <tbody>
            {metaLists.map((meta) => {
              const chk = checkList.find((x) => x.no === meta.id);
              if (!chk) return <React.Fragment key={`none_${meta.id}`}></React.Fragment>;
              chk.title = meta.name; // 項目のテキストが変わっている場合アップデートする
              return chk.results.map((res, index) => (
                <tr key={`chklst_${chk.no}_${index}`}>
                  {index === 0 && (
                    <>
                      <TdCenter rowSpan={chk.results.length}>
                        <MyCheckbox
                          readonly={readonly}
                          initialValue={chk.checked}
                          changed={(value) => {
                            chk.checked = value;
                            onCheckListChanged();
                          }}
                        />
                      </TdCenter>
                      <TdCenter rowSpan={chk.results.length}>{chk.no}</TdCenter>
                      <td rowSpan={chk.results.length} style={{ alignContent: "center" }}>
                        {chk.title}
                      </td>
                    </>
                  )}
                  {getListBody(res, chk, index === 0)}
                </tr>
              ));
            })}
          </tbody>
        </STable>
      </RootDiv>
      {mapTarget && (
        <SelectMapAreaDlg
          title={"地区"}
          show={mapTarget !== null}
          lists={areas}
          initialAreas={mapTarget.areas}
          initialPoints={mapTarget.point}
          areaSelected={(vals) => {
            mapTarget.areas = vals;
            mapTarget.point = undefined;
            setMapTarget(null);
          }}
          pointSelected={(point: MapPoint) => {
            mapTarget.point = point;
            mapTarget.areas = [];
            setMapTarget(null);
          }}
          onCancel={() => setMapTarget(null)}
        />
      )}
    </>
  );
};

const RootDiv = styled.div`
  width: 100%;
  max-height: 400px;
  overflow: auto;
`;

const STable = styled(Table)`
  td {
    padding-top: 0.1rem;
    padding-bottom: 0.1rem;
    vertical-align: middle;
  }
  thead {
    position: sticky;
    top: 0;
    z-index: 1;
  }
`;

const TdCenter = styled.td`
  vertical-align: middle;
  text-align: center;
`;

const SMyTextArea = styled(MyTextArea)`
  font-size: 14px;
`;

const Sth1 = styled.th`
  min-width: 60px;
`;

const Sth2 = styled.th`
  min-width: 80px;
`;

const Sth3 = styled.th`
  min-width: 140px;
`;
